<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { useRouter } from 'vue-router';
import addDays from 'date-fns/addDays';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import endOfDay from 'date-fns/endOfDay';
import format from 'date-fns/format';
import fromUnixTime from 'date-fns/fromUnixTime';
import getUnixTime from 'date-fns/getUnixTime';
import startOfDay from 'date-fns/startOfDay';
import subDays from 'date-fns/subDays';
import { getQuantileIntervals } from '@chatwoot/utils';
import reportsAPI from 'dashboard/api/reports';
import {
  downloadCsvFile,
  generateFileName,
} from 'dashboard/helper/downloadHelper';
import BarChart from 'shared/components/charts/BarChart.vue';
import WootDateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';
import { DATE_RANGE_OPTIONS } from './constants';

const DISPLAYS_METRIC_KEY = 'campaign_displays_count';
const CHATS_METRIC_KEY = 'campaign_chats_count';
const HEATMAP_METRIC_KEY = CHATS_METRIC_KEY;
const HEATMAP_QUANTILES = [0.2, 0.4, 0.6, 0.8, 0.9, 0.99];
const SUPPORTED_RANGE_IDS = [
  DATE_RANGE_OPTIONS.TODAY.id,
  DATE_RANGE_OPTIONS.YESTERDAY.id,
  DATE_RANGE_OPTIONS.LAST_7_DAYS.id,
  DATE_RANGE_OPTIONS.LAST_30_DAYS.id,
  DATE_RANGE_OPTIONS.LAST_3_MONTHS.id,
  DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE.id,
];

const DATE_RANGE_BY_ID = Object.values(DATE_RANGE_OPTIONS).reduce(
  (acc, option) => {
    acc[option.id] = option;
    return acc;
  },
  {}
);

const router = useRouter();
const { t } = useI18n();

const selectedDateRangeId = ref(DATE_RANGE_OPTIONS.LAST_7_DAYS.id);
const isHourlyDistributionEnabled = ref(false);
const isLoading = ref(false);
const isHeatmapLoading = ref(false);
const errorMessage = ref('');
const currentDisplaysSeries = ref([]);
const previousDisplaysSeries = ref([]);
const currentChatsSeries = ref([]);
const previousChatsSeries = ref([]);
const currentSummary = ref({});
const previousSummary = ref({});
const campaignsBreakdownRows = ref([]);
const heatmapSeries = ref([]);
const generatedAt = ref(null);
const customDateRange = ref([new Date(), new Date()]);

const [showDateRangeMenu, toggleDateRangeMenu] = useToggle(false);

const selectedDateRange = computed(() => {
  return (
    DATE_RANGE_BY_ID[selectedDateRangeId.value] ||
    DATE_RANGE_OPTIONS.LAST_7_DAYS
  );
});

const dateRangeMenuItems = computed(() => {
  return SUPPORTED_RANGE_IDS.map(id => {
    const option = DATE_RANGE_BY_ID[id];
    return {
      action: 'select-date-range',
      value: id,
      label: t(option.translationKey),
      isSelected: id === selectedDateRangeId.value,
    };
  });
});

const selectedDateRangeLabel = computed(() =>
  t(selectedDateRange.value.translationKey)
);

const isCustomDateRange = computed(
  () => selectedDateRangeId.value === DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE.id
);

const isValidCustomDateRange = value =>
  Array.isArray(value) &&
  value.length === 2 &&
  value[0] instanceof Date &&
  !Number.isNaN(value[0].getTime()) &&
  value[1] instanceof Date &&
  !Number.isNaN(value[1].getTime());

const isDatePickerPopupClick = target =>
  target instanceof Element && Boolean(target.closest('.mx-datepicker-popup'));

const handleDateMenuClickOutside = event => {
  const target = event?.target;
  if (isDatePickerPopupClick(target)) return;
  toggleDateRangeMenu(false);
};

const getCurrentPeriod = () => {
  if (
    isCustomDateRange.value &&
    isValidCustomDateRange(customDateRange.value)
  ) {
    const fromDate = startOfDay(customDateRange.value[0]);
    const toDate = endOfDay(customDateRange.value[1]);
    const days = Math.max(differenceInCalendarDays(toDate, fromDate) + 1, 1);

    return {
      from: getUnixTime(fromDate),
      to: getUnixTime(toDate),
      fromDate,
      toDate,
      days,
    };
  }

  const option = selectedDateRange.value;
  const startOffset = option.startOffset ?? option.offset ?? 0;
  const endOffset = option.endOffset ?? option.offset ?? 0;

  const toDate = endOfDay(subDays(new Date(), endOffset));
  const fromDate = startOfDay(subDays(new Date(), startOffset));
  const days = differenceInCalendarDays(toDate, fromDate) + 1;

  return {
    from: getUnixTime(fromDate),
    to: getUnixTime(toDate),
    fromDate,
    toDate,
    days,
  };
};

const currentPeriod = computed(() => getCurrentPeriod());

const previousPeriod = computed(() => {
  const previousToDate = endOfDay(subDays(currentPeriod.value.fromDate, 1));
  const previousFromDate = startOfDay(
    subDays(previousToDate, currentPeriod.value.days - 1)
  );

  return {
    from: getUnixTime(previousFromDate),
    to: getUnixTime(previousToDate),
    fromDate: previousFromDate,
    toDate: previousToDate,
    days: currentPeriod.value.days,
  };
});

const normalizeSeries = series => {
  if (!Array.isArray(series)) return [];
  return [...series].sort((left, right) => left.timestamp - right.timestamp);
};

const formatPeriodLabel = period => {
  return `${format(fromUnixTime(period.from), 'dd MMM yy')} - ${format(
    fromUnixTime(period.to),
    'dd MMM yy'
  )}`;
};

const currentPeriodLabel = computed(() =>
  formatPeriodLabel(currentPeriod.value)
);
const previousPeriodLabel = computed(() =>
  formatPeriodLabel(previousPeriod.value)
);

const sumSeries = series =>
  series.reduce((sum, item) => sum + Number(item.value || 0), 0);

const currentDisplays = computed(() => {
  const summaryValue = Number(
    currentSummary.value?.campaign_displays_count || 0
  );
  if (summaryValue) return summaryValue;
  return sumSeries(currentDisplaysSeries.value);
});

const previousDisplays = computed(() => {
  const summaryValue = Number(
    previousSummary.value?.campaign_displays_count || 0
  );
  if (summaryValue) return summaryValue;
  return sumSeries(previousDisplaysSeries.value);
});

const currentChats = computed(() => {
  const summaryValue = Number(currentSummary.value?.campaign_chats_count || 0);
  if (summaryValue) return summaryValue;
  return sumSeries(currentChatsSeries.value);
});

const previousChats = computed(() => {
  const summaryValue = Number(previousSummary.value?.campaign_chats_count || 0);
  if (summaryValue) return summaryValue;
  return sumSeries(previousChatsSeries.value);
});

const currentConversion = computed(() => {
  if (!currentDisplays.value) return null;
  return ((currentChats.value / currentDisplays.value) * 100).toFixed(2);
});

const previousConversion = computed(() => {
  if (!previousDisplays.value) return null;
  return ((previousChats.value / previousDisplays.value) * 100).toFixed(2);
});

const comparisonRows = computed(() => {
  const maxLength = Math.max(
    currentDisplaysSeries.value.length,
    previousDisplaysSeries.value.length,
    currentPeriod.value.days
  );

  return Array.from({ length: maxLength }, (_, index) => {
    const currentDefaultTimestamp = getUnixTime(
      addDays(currentPeriod.value.fromDate, index)
    );
    const previousDefaultTimestamp = getUnixTime(
      addDays(previousPeriod.value.fromDate, index)
    );

    const currentDisplayed = Number(
      currentDisplaysSeries.value[index]?.value || 0
    );
    const previousDisplayed = Number(
      previousDisplaysSeries.value[index]?.value || 0
    );
    const currentChat = Number(currentChatsSeries.value[index]?.value || 0);
    const previousChat = Number(previousChatsSeries.value[index]?.value || 0);

    return {
      currentTimestamp:
        currentDisplaysSeries.value[index]?.timestamp ||
        currentDefaultTimestamp,
      previousTimestamp:
        previousDisplaysSeries.value[index]?.timestamp ||
        previousDefaultTimestamp,
      currentDisplayed,
      previousDisplayed,
      currentChat,
      previousChat,
      currentDateLabel: format(
        fromUnixTime(
          currentDisplaysSeries.value[index]?.timestamp ||
            currentDefaultTimestamp
        ),
        'dd MMM yy'
      ),
      previousDateLabel: format(
        fromUnixTime(
          previousDisplaysSeries.value[index]?.timestamp ||
            previousDefaultTimestamp
        ),
        'dd MMM yy'
      ),
    };
  });
});

const chartCollection = computed(() => ({
  labels: comparisonRows.value.map(row =>
    format(fromUnixTime(row.currentTimestamp), 'dd MMM')
  ),
  datasets: [
    {
      label: `${t('CAMPAIGNS_CONVERSION_REPORT.DISPLAYED')} (${currentPeriodLabel.value})`,
      data: comparisonRows.value.map(row => row.currentDisplayed),
      backgroundColor: '#1D75E5',
      borderRadius: 4,
      categoryPercentage: 0.7,
      barPercentage: 0.9,
    },
    {
      label: `${t('CAMPAIGNS_CONVERSION_REPORT.DISPLAYED')} (${previousPeriodLabel.value})`,
      data: comparisonRows.value.map(row => row.previousDisplayed),
      backgroundColor: '#E7C51B',
      borderRadius: 4,
      categoryPercentage: 0.7,
      barPercentage: 0.9,
    },
    {
      label: `${t('CAMPAIGNS_CONVERSION_REPORT.CHATS')} (${currentPeriodLabel.value})`,
      data: comparisonRows.value.map(row => row.currentChat),
      backgroundColor: '#C23881',
      borderRadius: 4,
      categoryPercentage: 0.7,
      barPercentage: 0.9,
    },
    {
      label: `${t('CAMPAIGNS_CONVERSION_REPORT.CHATS')} (${previousPeriodLabel.value})`,
      data: comparisonRows.value.map(row => row.previousChat),
      backgroundColor: '#8B93A1',
      borderRadius: 4,
      categoryPercentage: 0.7,
      barPercentage: 0.9,
    },
  ],
}));

const chartOptions = {
  plugins: {
    legend: {
      display: false,
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        callback: value => Number(value).toLocaleString(),
      },
    },
  },
};

const showEmptyState = computed(() => currentChats.value === 0);

const dayColumns = computed(() => {
  return Array.from({ length: currentPeriod.value.days }, (_, index) => {
    const date = addDays(currentPeriod.value.fromDate, index);
    return {
      key: format(date, 'yyyy-MM-dd'),
      label: format(date, 'dd MMM'),
      tooltipLabel: format(date, 'dd MMM yy'),
    };
  });
});

const hourlyRows = computed(() => {
  return Array.from({ length: 24 }, (_, hour) => hour);
});

const heatmapValueMap = computed(() => {
  return heatmapSeries.value.reduce((acc, item) => {
    const date = fromUnixTime(item.timestamp);
    const dayKey = format(date, 'yyyy-MM-dd');
    const key = `${dayKey}-${date.getHours()}`;
    acc[key] = Number(item.value || 0);
    return acc;
  }, {});
});

const heatmapRows = computed(() => {
  return hourlyRows.value.map(hour => {
    return {
      hour,
      cells: dayColumns.value.map(day => {
        const key = `${day.key}-${hour}`;
        const value = heatmapValueMap.value[key] || 0;
        return {
          key,
          value,
          dayLabel: day.tooltipLabel,
        };
      }),
    };
  });
});

const heatmapValues = computed(() =>
  Object.values(heatmapValueMap.value).map(value => Number(value || 0))
);

const heatmapQuantiles = computed(() =>
  getQuantileIntervals(heatmapValues.value, HEATMAP_QUANTILES)
);

const getHeatmapCellClass = value => {
  if (!value) return 'bg-n-slate-2';

  let level = [...heatmapQuantiles.value, Infinity].findIndex(
    range => value <= range && value > 0
  );

  if (level > 6) level = 5;
  if (level === 0) return 'bg-n-slate-2';

  const classes = [
    'bg-n-blue-3',
    'bg-n-blue-5',
    'bg-n-blue-7',
    'bg-n-blue-8',
    'bg-n-blue-10',
    'bg-n-blue-11',
  ];

  return classes[level - 1];
};

const formatHourLabel = hour => {
  const hour12 = hour % 12 || 12;
  const suffix = hour < 12 ? 'am' : 'pm';
  return `${hour12}:00 ${suffix}`;
};

const generatedAtLabel = computed(() => {
  if (!generatedAt.value) {
    return t('LAST_7_DAYS_REPORT.NOT_GENERATED_YET');
  }

  return generatedAt.value.toLocaleString();
});

const toCsvValue = value => {
  const stringValue = String(value ?? '');
  if (/[",\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

const rowsToCsv = (headers, rows) => {
  const csvRows = [headers, ...rows];
  return csvRows
    .map(row => row.map(column => toCsvValue(column)).join(','))
    .join('\n');
};

const downloadBreakdownCsv = () => {
  const headers = [
    t('CAMPAIGNS_CONVERSION_REPORT.CAMPAIGN'),
    t('CAMPAIGNS_CONVERSION_REPORT.GROUP'),
    t('CAMPAIGNS_CONVERSION_REPORT.DISPLAYED'),
    t('CAMPAIGNS_CONVERSION_REPORT.CHATS'),
    t('CAMPAIGNS_CONVERSION_REPORT.CONVERSION'),
  ];

  const rows = campaignsBreakdownRows.value.map(row => [
    row.title,
    row.group_name,
    row.displayed,
    row.chats,
    row.conversion_label,
  ]);

  const csvContent = rowsToCsv(headers, rows);
  const fileName = generateFileName({
    type: 'campaigns-conversion-breakdown',
    to: currentPeriod.value.to,
  });
  downloadCsvFile(fileName, csvContent);
};

const downloadHeatmapCsv = () => {
  const headers = [
    t('TOTAL_CHATS_REPORT.HOUR'),
    ...dayColumns.value.map(day => day.label),
  ];
  const rows = heatmapRows.value.map(row => [
    formatHourLabel(row.hour),
    ...row.cells.map(cell => cell.value),
  ]);
  const csvContent = rowsToCsv(headers, rows);
  const fileName = generateFileName({
    type: 'campaigns-conversion-heatmap',
    to: currentPeriod.value.to,
  });
  downloadCsvFile(fileName, csvContent);
};

const goToCampaigns = () => {
  router.push({ name: 'campaigns_livechat_index' });
};

const goToCampaignsEdit = () => {
  router.push({ name: 'campaigns_livechat_index' });
};

const fetchTimeseriesAndSummary = async () => {
  isLoading.value = true;
  errorMessage.value = '';

  try {
    const [
      currentDisplaysResponse,
      previousDisplaysResponse,
      currentChatsResponse,
      previousChatsResponse,
    ] = await Promise.all([
      reportsAPI.getReports({
        metric: DISPLAYS_METRIC_KEY,
        from: currentPeriod.value.from,
        to: currentPeriod.value.to,
        groupBy: 'day',
        businessHours: false,
      }),
      reportsAPI.getReports({
        metric: DISPLAYS_METRIC_KEY,
        from: previousPeriod.value.from,
        to: previousPeriod.value.to,
        groupBy: 'day',
        businessHours: false,
      }),
      reportsAPI.getReports({
        metric: CHATS_METRIC_KEY,
        from: currentPeriod.value.from,
        to: currentPeriod.value.to,
        groupBy: 'day',
        businessHours: false,
      }),
      reportsAPI.getReports({
        metric: CHATS_METRIC_KEY,
        from: previousPeriod.value.from,
        to: previousPeriod.value.to,
        groupBy: 'day',
        businessHours: false,
      }),
    ]);

    const [currentSummaryResponse, previousSummaryResponse] = await Promise.all(
      [
        reportsAPI.getSummary(
          currentPeriod.value.from,
          currentPeriod.value.to,
          'account',
          null,
          'day',
          false,
          false
        ),
        reportsAPI.getSummary(
          previousPeriod.value.from,
          previousPeriod.value.to,
          'account',
          null,
          'day',
          false,
          false
        ),
      ]
    );

    currentDisplaysSeries.value = normalizeSeries(currentDisplaysResponse.data);
    previousDisplaysSeries.value = normalizeSeries(
      previousDisplaysResponse.data
    );
    currentChatsSeries.value = normalizeSeries(currentChatsResponse.data);
    previousChatsSeries.value = normalizeSeries(previousChatsResponse.data);
    currentSummary.value = currentSummaryResponse.data || {};
    previousSummary.value = previousSummaryResponse.data || {};
    generatedAt.value = new Date();
  } catch {
    errorMessage.value = t('CAMPAIGNS_CONVERSION_REPORT.FETCH_ERROR');
    currentDisplaysSeries.value = [];
    previousDisplaysSeries.value = [];
    currentChatsSeries.value = [];
    previousChatsSeries.value = [];
    currentSummary.value = {};
    previousSummary.value = {};
  } finally {
    isLoading.value = false;
  }
};

const fetchCampaignBreakdown = async () => {
  try {
    const response = await reportsAPI.getCampaignsConversion({
      from: currentPeriod.value.from,
      to: currentPeriod.value.to,
    });

    campaignsBreakdownRows.value = (response.data?.items || []).map(item => {
      return {
        ...item,
        conversion_label:
          item.conversion === null || item.conversion === undefined
            ? '-'
            : `${Number(item.conversion).toFixed(2)}%`,
      };
    });
  } catch {
    campaignsBreakdownRows.value = [];
  }
};

const fetchHeatmapData = async () => {
  isHeatmapLoading.value = true;
  errorMessage.value = '';

  try {
    const response = await reportsAPI.getReports({
      metric: HEATMAP_METRIC_KEY,
      from: currentPeriod.value.from,
      to: currentPeriod.value.to,
      groupBy: 'hour',
      businessHours: false,
    });

    heatmapSeries.value = normalizeSeries(response.data);
    generatedAt.value = new Date();
  } catch {
    errorMessage.value = t('CAMPAIGNS_CONVERSION_REPORT.FETCH_ERROR');
    heatmapSeries.value = [];
  } finally {
    isHeatmapLoading.value = false;
  }
};

const refreshData = async () => {
  await Promise.all([fetchTimeseriesAndSummary(), fetchCampaignBreakdown()]);
  if (isHourlyDistributionEnabled.value) {
    await fetchHeatmapData();
  }
};

const onSelectDateRange = value => {
  if (value === DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE.id) {
    selectedDateRangeId.value = value;
    return;
  }
  toggleDateRangeMenu(false);
  selectedDateRangeId.value = value;
};

const onCustomDateChange = value => {
  if (!isValidCustomDateRange(value)) return;
  customDateRange.value = value;
  toggleDateRangeMenu(false);
};

watch(selectedDateRangeId, async () => {
  await refreshData();
});

watch(customDateRange, async () => {
  if (
    isCustomDateRange.value &&
    isValidCustomDateRange(customDateRange.value)
  ) {
    await refreshData();
  }
});

watch(isHourlyDistributionEnabled, async enabled => {
  if (!enabled) return;
  await fetchHeatmapData();
});

onMounted(async () => {
  await refreshData();
});
</script>

<template>
  <div class="cc-page">
    <div class="cc-shell">
      <div class="cc-header">
        <div class="cc-header-left">
          <h1 class="cc-title">{{ t('CAMPAIGNS_CONVERSION_REPORT.TITLE') }}</h1>
        </div>
      </div>

      <div class="cc-toolbar">
        <div
          v-on-click-outside="handleDateMenuClickOutside"
          class="cc-toolbar-left"
        >
          <button
            class="cc-secondary-btn"
            type="button"
            @click="toggleDateRangeMenu()"
          >
            <span>{{ selectedDateRangeLabel }}</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              width="16"
              height="16"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="m6 9 6 6 6-6"
              />
            </svg>
          </button>

          <div v-if="showDateRangeMenu" class="cc-menu">
            <button
              v-for="item in dateRangeMenuItems"
              :key="item.value"
              class="cc-menu-item"
              :class="{ 'is-selected': item.isSelected }"
              type="button"
              @click="onSelectDateRange(item.value)"
            >
              {{ item.label }}
            </button>
            <div v-if="isCustomDateRange" class="cc-menu-custom">
              <WootDateRangePicker
                show-range
                class="cc-date-picker"
                :value="customDateRange"
                :confirm-text="$t('REPORT.CUSTOM_DATE_RANGE.CONFIRM')"
                :placeholder="$t('REPORT.CUSTOM_DATE_RANGE.PLACEHOLDER')"
                @change="onCustomDateChange"
              />
            </div>
          </div>
        </div>

        <div class="cc-toolbar-right">
          <span class="cc-toggle-label">
            {{ t('TOTAL_CHATS_REPORT.TWENTY_FOUR_HOUR_DISTRIBUTION') }}
          </span>
          <button
            class="crt-switch"
            :class="{ 'is-on': isHourlyDistributionEnabled }"
            type="button"
            @click="isHourlyDistributionEnabled = !isHourlyDistributionEnabled"
          >
            <span />
          </button>
        </div>
      </div>

      <div class="cc-content">
        <div v-if="errorMessage" class="cc-error">
          {{ errorMessage }}
        </div>

        <section class="cc-card">
          <div class="cc-card-header">
            <div class="cc-card-title-wrap">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8h.01M11 12h1v4h1M3 12a9 9 0 1 0 18.001 0A9 9 0 0 0 3 12"
                />
              </svg>
              <h2 class="cc-card-title">
                {{ t('CAMPAIGNS_CONVERSION_REPORT.TITLE') }}
              </h2>
              <span class="cc-period">{{ currentPeriodLabel }}</span>
            </div>
            <button
              class="cc-export-btn"
              type="button"
              @click="downloadBreakdownCsv"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="16"
                height="16"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2m-8-1V4m0 12 5-5m-5 5-5-5"
                />
              </svg>
              <span>{{ t('CAMPAIGNS_CONVERSION_REPORT.EXPORT_CSV') }}</span>
            </button>
          </div>

          <div class="cc-section cc-section--no-bottom">
            <div class="cc-metric-row">
              <div class="cc-metric">
                <div class="cc-metric-label">
                  {{ t('CAMPAIGNS_CONVERSION_REPORT.DISPLAYED') }}
                </div>
                <div class="cc-metric-sub">{{ currentPeriodLabel }}</div>
                <div class="cc-metric-value">
                  {{ currentDisplays.toLocaleString() }}
                </div>
              </div>
              <div class="cc-metric">
                <div class="cc-metric-label">
                  {{ t('CAMPAIGNS_CONVERSION_REPORT.CHATS') }}
                </div>
                <div class="cc-metric-sub">{{ currentPeriodLabel }}</div>
                <div class="cc-metric-value">
                  {{ currentChats.toLocaleString() }}
                </div>
              </div>
              <div class="cc-metric">
                <div class="cc-metric-label">
                  {{ t('CAMPAIGNS_CONVERSION_REPORT.CONVERSION') }}
                </div>
                <div class="cc-metric-sub">{{ currentPeriodLabel }}</div>
                <div class="cc-metric-value">
                  {{ currentConversion ? `${currentConversion}%` : '—' }}
                </div>
              </div>
            </div>

            <div class="cc-chart">
              <woot-loading-state
                v-if="isLoading"
                :message="$t('REPORT.LOADING_CHART')"
                class="h-full"
              />

              <div v-else-if="showEmptyState" class="cc-empty cc-empty--stack">
                <div class="cc-empty__title">
                  {{ t('CAMPAIGNS_CONVERSION_REPORT.EMPTY_TITLE') }}
                </div>
                <div class="cc-empty__sub">
                  {{ t('CAMPAIGNS_CONVERSION_REPORT.EMPTY_SUBTITLE') }}
                </div>
                <button
                  class="cc-primary-btn"
                  type="button"
                  @click="goToCampaigns"
                >
                  {{ t('CAMPAIGNS_CONVERSION_REPORT.GO_TO_CAMPAIGNS') }}
                </button>
              </div>

              <BarChart
                v-else
                :collection="chartCollection"
                :chart-options="chartOptions"
              />
            </div>
          </div>

          <div class="cc-breakdown">
            <div class="cc-breakdown-header">
              <div class="cc-breakdown-title">
                {{ t('CAMPAIGNS_CONVERSION_REPORT.BREAKDOWN_TITLE') }}
              </div>
              <button
                class="cc-export-btn"
                type="button"
                @click="downloadBreakdownCsv"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2m-8-1V4m0 12 5-5m-5 5-5-5"
                  />
                </svg>
                <span>{{ t('CAMPAIGNS_CONVERSION_REPORT.EXPORT_CSV') }}</span>
              </button>
            </div>

            <div class="cc-table-wrap">
              <table class="cc-table">
                <thead>
                  <tr>
                    <th class="cc-th">
                      {{ t('CAMPAIGNS_CONVERSION_REPORT.CAMPAIGN') }}
                    </th>
                    <th class="cc-th">
                      {{ t('CAMPAIGNS_CONVERSION_REPORT.GROUP') }}
                    </th>
                    <th class="cc-th cc-th--num">
                      {{ t('CAMPAIGNS_CONVERSION_REPORT.DISPLAYED') }}
                    </th>
                    <th class="cc-th cc-th--num">
                      {{ t('CAMPAIGNS_CONVERSION_REPORT.CHATS') }}
                    </th>
                    <th class="cc-th cc-th--num">
                      {{ t('CAMPAIGNS_CONVERSION_REPORT.CONVERSION') }}
                    </th>
                    <th class="cc-th">
                      {{ t('CAMPAIGNS_CONVERSION_REPORT.SETTINGS') }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in campaignsBreakdownRows"
                    :key="row.campaign_id"
                  >
                    <td class="cc-td">{{ row.title }}</td>
                    <td class="cc-td">{{ row.group_name || '—' }}</td>
                    <td class="cc-td cc-td--num">
                      {{ Number(row.displayed || 0).toLocaleString() }}
                    </td>
                    <td class="cc-td cc-td--num">
                      {{ Number(row.chats || 0).toLocaleString() }}
                    </td>
                    <td class="cc-td cc-td--num">{{ row.conversion_label }}</td>
                    <td class="cc-td">
                      <button
                        class="cc-secondary-action"
                        type="button"
                        @click="goToCampaignsEdit()"
                      >
                        {{ t('CAMPAIGNS_CONVERSION_REPORT.EDIT') }}
                      </button>
                    </td>
                  </tr>
                  <tr v-if="!campaignsBreakdownRows.length">
                    <td colspan="6" class="cc-td cc-td--empty">
                      {{ t('CAMPAIGNS_CONVERSION_REPORT.NO_DATA') }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section v-if="isHourlyDistributionEnabled" class="cc-card">
          <div class="cc-card-header">
            <div class="cc-card-title-wrap">
              <h2 class="cc-card-title">
                {{ t('CAMPAIGNS_CONVERSION_REPORT.HEATMAP_TITLE') }}
              </h2>
              <span class="cc-subtitle">{{
                t('CAMPAIGNS_CONVERSION_REPORT.HEATMAP_LIMITATION')
              }}</span>
            </div>
            <button
              class="cc-export-btn"
              type="button"
              @click="downloadHeatmapCsv"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="16"
                height="16"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2m-8-1V4m0 12 5-5m-5 5-5-5"
                />
              </svg>
              <span>{{ t('CAMPAIGNS_CONVERSION_REPORT.EXPORT_CSV') }}</span>
            </button>
          </div>

          <div class="cc-section">
            <woot-loading-state
              v-if="isHeatmapLoading"
              :message="$t('REPORT.LOADING_CHART')"
            />

            <div
              v-else-if="!heatmapRows.length"
              class="cc-empty cc-empty--short"
            >
              {{ t('CAMPAIGNS_CONVERSION_REPORT.NO_DATA') }}
            </div>

            <div v-else class="cc-heatmap-wrap">
              <table class="cc-heatmap-table">
                <thead>
                  <tr>
                    <th class="cc-heatmap-th cc-heatmap-th--hour">
                      {{ t('TOTAL_CHATS_REPORT.HOUR') }}
                    </th>
                    <th
                      v-for="day in dayColumns"
                      :key="day.key"
                      class="cc-heatmap-th"
                    >
                      {{ day.label }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in heatmapRows" :key="`hour-${row.hour}`">
                    <td class="cc-heatmap-td cc-heatmap-td--hour">
                      {{ row.hour % 3 === 0 ? formatHourLabel(row.hour) : '' }}
                    </td>
                    <td
                      v-for="cell in row.cells"
                      :key="cell.key"
                      class="cc-heatmap-td"
                      :title="`${cell.dayLabel} ${formatHourLabel(row.hour)}: ${cell.value}`"
                    >
                      <div
                        class="cc-heatmap-cell"
                        :class="getHeatmapCellClass(cell.value)"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <p class="cc-footer-note">
          {{
            t('CAMPAIGNS_CONVERSION_REPORT.GENERATED_AT', {
              time: generatedAtLabel,
            })
          }}
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.cc-page {
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  background: #ffffff;
  color: #111827;
  overflow: auto;
}
.cc-shell {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  overflow: visible;
  min-width: 650px;
}

.cc-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: 58px;
  padding: 0 24px;
  border-bottom: 1px solid #eceff3;
  position: sticky;
  top: 0;
  z-index: 50;
  background: #ffffff;
}
.cc-header-left {
  display: flex;
  align-items: center;
  gap: 0;
}
.cc-title {
  margin: 0;
  font-size: 18px !important;
  line-height: 24px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #111827;
}
.cc-toolbar {
  padding: 12px 24px 8px;
  background: #ffffff;
  box-shadow: rgba(0, 0, 0, 0.3) 0 3px 10px -6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  position: sticky;
  top: 58px;
  z-index: 49;
}
.cc-toolbar-left {
  position: relative;
}
.cc-toolbar-right {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}
.cc-toggle-label {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.cc-secondary-btn {
  height: 34px;
  border-radius: 8px;
  border: 1px solid #d9dde3;
  background: #ffffff;
  color: #111827;
  font-size: 12px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  cursor: pointer;
}
.cc-secondary-btn:hover {
  background: #f9fafb;
}
.cc-menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  min-width: 220px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: rgba(0, 0, 0, 0.12) 0 14px 38px;
  padding: 6px;
  z-index: 30;
}
.cc-menu-item {
  width: 100%;
  text-align: left;
  padding: 8px 10px;
  border-radius: 8px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}
.cc-menu-item:hover {
  background: #f3f4f6;
}
.cc-menu-item.is-selected {
  background: #eef2ff;
  color: #1d4ed8;
}

.cc-menu-custom {
  margin-top: 8px;
  border-top: 1px solid #eef0f3;
  padding-top: 8px;
}

.cc-date-picker {
  width: 100%;
}

.cc-content {
  padding: 16px 32px;
  overflow-x: hidden;
}
.cc-error {
  margin-bottom: 12px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
}

.cc-card {
  border-radius: 4px;
  border: 1px solid #d8dee5;
  background: #ffffff;
  box-sizing: border-box;
  margin-bottom: 20px;
  overflow: hidden;
}
.cc-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid #e5e7eb;
}
.cc-card-title-wrap {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex-wrap: wrap;
}
.cc-card-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}
.cc-period {
  font-size: 12px;
  color: #6b7280;
  margin-left: 10px;
  white-space: nowrap;
}
.cc-subtitle {
  font-size: 12px;
  color: #6b7280;
  margin-left: 10px;
}

.cc-export-btn {
  min-width: 128px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #111827;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}
.cc-export-btn:hover {
  background: #f9fafb;
}

.cc-section {
  padding: 16px 20px 20px;
}
.cc-section--no-bottom {
  padding-bottom: 0;
}

.cc-metric-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}
@media (max-width: 1100px) {
  .cc-metric-row {
    grid-template-columns: 1fr;
  }
}
.cc-metric {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f8fafc;
  padding: 12px;
}
.cc-metric-label {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}
.cc-metric-sub {
  margin-top: 2px;
  font-size: 12px;
  color: #6b7280;
}
.cc-metric-value {
  margin-top: 10px;
  font-size: 28px;
  line-height: 32px;
  font-weight: 800;
  color: #111827;
}

.cc-chart {
  height: 384px;
  margin-top: 16px;
}
.cc-empty {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 13px;
}
.cc-empty--short {
  height: 160px;
}
.cc-empty--stack {
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  text-align: center;
}
.cc-empty__title {
  font-size: 18px;
  font-weight: 800;
  color: #111827;
}
.cc-empty__sub {
  font-size: 13px;
  color: #6b7280;
  max-width: 520px;
}
.cc-primary-btn {
  height: 36px;
  border-radius: 10px;
  border: 1px solid #111827;
  background: #111827;
  color: #ffffff;
  padding: 0 14px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}
.cc-primary-btn:hover {
  opacity: 0.92;
}

.cc-breakdown {
  border-top: 1px solid #dfe3e8;
}
.cc-breakdown-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 15px 16px;
}
.cc-breakdown-title {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
}

.cc-table-wrap {
  overflow-x: auto;
  border-top: 1px solid #dfe3e8;
}
.cc-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.cc-th {
  padding: 10px 12px;
  text-align: left;
  font-weight: 800;
  color: #111827;
  border-bottom: 1px solid #edf1f5;
  background: #f8fafc;
  white-space: nowrap;
}
.cc-th--num {
  text-align: right;
}
.cc-td {
  padding: 12px;
  border-bottom: 1px solid #edf1f5;
  color: #111827;
  vertical-align: middle;
}
.cc-td--num {
  text-align: right;
  white-space: nowrap;
}
.cc-td--empty {
  padding: 24px;
  text-align: center;
  color: #6b7280;
}
.cc-secondary-action {
  height: 30px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #111827;
  font-size: 12px;
  font-weight: 700;
  padding: 0 10px;
  cursor: pointer;
}
.cc-secondary-action:hover {
  background: #f9fafb;
}

.cc-heatmap-wrap {
  overflow-x: auto;
}
.cc-heatmap-table {
  width: max-content;
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 4px;
}
.cc-heatmap-th {
  padding: 4px 6px;
  text-align: center;
  font-size: 12px;
  font-weight: 800;
  color: #111827;
}
.cc-heatmap-th--hour {
  text-align: left;
  color: #6b7280;
}
.cc-heatmap-td {
  padding: 2px;
}
.cc-heatmap-td--hour {
  font-size: 12px;
  color: #6b7280;
  padding-right: 10px;
  white-space: nowrap;
}
.cc-heatmap-cell {
  height: 24px;
  width: 56px;
  border-radius: 4px;
}

.cc-footer-note {
  margin-top: 8px;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
}

.crt-switch {
  width: 27px;
  height: 20px;
  border: none;
  border-radius: 15px;
  box-sizing: border-box;
  cursor: pointer;
  background: rgb(221, 221, 221);
  padding: 0;
  display: block;
  position: relative;
}

.crt-switch span {
  position: absolute;
  display: block;
  top: 2px;
  left: 2px;
  border-radius: 50%;
  background-color: rgb(255, 255, 255);
  box-shadow:
    rgba(0, 0, 0, 0.05) 0 3px 3px 0,
    rgba(0, 0, 0, 0.1) 0 2px 2px 0,
    rgba(0, 0, 0, 0.05) 0 3px 2px 0;
  transition:
    left 0.2s ease-in-out,
    background-color 0.2s ease-in-out;
  will-change: left, background-color;
  width: 15px;
  height: 15px;
}

.crt-switch.is-on {
  background: #2f80ed;
}

.crt-switch.is-on span {
  left: 10px;
}

::global(body.dark) .cc-page,
::global(body.dark) .cc-shell,
::global(body.dark) .cc-card,
::global(body.dark) .cc-header,
::global(body.dark) .cc-toolbar,
::global(body.dark) .cc-th,
::global(body.dark) .cc-td,
::global(body.dark) .cc-breakdown-title,
::global(body.dark) .cc-table-wrap,
::global(body.theme-mix) .cc-page,
::global(body.theme-mix) .cc-shell,
::global(body.theme-mix) .cc-card,
::global(body.theme-mix) .cc-header,
::global(body.theme-mix) .cc-toolbar,
::global(body.theme-mix) .cc-th,
::global(body.theme-mix) .cc-td,
::global(body.theme-mix) .cc-breakdown-title,
::global(body.theme-mix) .cc-table-wrap {
  background: #ffffff !important;
  color: #111827 !important;
}

::global(body.dark) .cc-th *,
::global(body.dark) .cc-heatmap-th,
::global(body.dark) .cc-heatmap-td,
::global(body.theme-mix) .cc-th *,
::global(body.theme-mix) .cc-heatmap-th,
::global(body.theme-mix) .cc-heatmap-td {
  color: #111827 !important;
}
</style>
